import { useCallback, useState } from 'react';
import { useAuthenticatedFetch } from './useAuthenticatedFetch';
import type { ApiResponse, SubscribeResponse } from '../types';

/**
 * Start the merchant's plan now, ending the trial early.
 *
 * Shopify cannot shorten a trial, so this asks the backend for a fresh
 * subscription without one and sends the merchant to Shopify's approval page.
 * Nothing is charged, and nothing changes, unless they approve it there.
 */
export function useConvertTrial() {
  const authenticatedFetch = useAuthenticatedFetch();
  const [converting, setConverting] = useState(false);
  const [failed, setFailed] = useState(false);

  const convertTrial = useCallback(async () => {
    setConverting(true);
    setFailed(false);

    try {
      const response = await authenticatedFetch('/api/billing/convert-trial', {
        method: 'POST',
      });

      const json: ApiResponse<SubscribeResponse> = await response
        .json()
        .catch(() => ({ success: false, error: `HTTP ${response.status}` }));

      if (!response.ok || !json.success || !json.data?.confirmationUrl) {
        throw new Error(json.error || `HTTP error! status: ${response.status}`);
      }

      // '_top' breaks out of the Shopify admin iframe; '_blank' is blocked as a
      // popup in embedded apps. The page navigates away here and comes back
      // through the billing callback once the merchant has decided.
      window.open(json.data.confirmationUrl, '_top');
    } catch (error) {
      console.error('Convert trial error:', error);
      setFailed(true);
      setConverting(false);
    }
  }, [authenticatedFetch]);

  return { convertTrial, converting, failed };
}
